import React, { useState, useEffect, useMemo } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {
  getStockTransferByBranch,
  deleteStockTransfer,
} from "../../services/StockApiServices";
import { useSelector } from "react-redux";
import StockTransferModal from "../../components/transactions/modal/StockTransferModal";
import { InvStockTransfer } from "../../core/json/InvStockTransfer";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, num } from "../../components/report/reportFormat";

const StockTransfer = () => {
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [stockList, setStockList] = useState([]);

  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Stock Transfer")
    );
  }, []);

  useEffect(() => {
    handlestockData();
  }, [branch]);

  const handlestockData = async () => {
    try {
      const response = await getStockTransferByBranch(branch);
      const stockData = Array.isArray(response) ? response : [];
      setStockList(stockData);
    } catch (error) {
      console.error(
        "Failed to fetch Stock adjustments",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  const MySwal = withReactContent(Swal);

  // Handle the click event to delete a Stock adjustments
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const objStockTrn = new InvStockTransfer();
          objStockTrn.id = obj?.id;
          objStockTrn.guid = obj?.guid;
          objStockTrn.branch = branch;
          objStockTrn.isDeleted = true;
          objStockTrn.updatedUser = `${userId?.id}`;
          const response = await deleteStockTransfer(objStockTrn);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The Stock Transfer has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlestockData();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error(
            "Error deleting reason:",
            error?.response?.data?.Message || "something went wrong"
          );
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditClick = async (guid) => {
    try {
      const response = getStockTrnByGuid(guid);
      setUpdateData(response);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching Stock adjustment by GUID",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  function getStockTrnByGuid(guid) {
    const filteredStock = stockList?.filter(
      (stockTrn) => stockTrn.guid === guid
    );

    return filteredStock[0];
  }

  /* ---------------- list ---------------- */
  // one flat record per transfer; the keys match the columns
  const rows = useMemo(
    () =>
      stockList.map((entry) => {
        const lines = entry?.invStockTransferDetails || [];
        return {
          guid: entry.guid,
          raw: entry,
          date: dateOf(entry?.date),
          billNo: entry?.invoiceNo || "",
          toBranch: entry?.toBranch?.name || "",
          toBranchGuid: entry?.toBranch?.guid,
          remarks: entry?.remarks || "",
          items: lines.length,
          qty: lines.reduce((a, l) => a + num(l?.quantity), 0),
          lines,
        };
      }),
    [stockList]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry No", type: "doc" },
      { k: "toBranch", t: "To Branch" },
      { k: "remarks", t: "Remarks" },
      { k: "items", t: "Items", type: "int", off: true },
      { k: "qty", t: "Quantity", type: "int", off: true },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Stock Transfer" }], []);

  // the branches this branch has transferred to
  const filters = useMemo(() => {
    const seen = new Map();
    stockList.forEach((e) => {
      const g = e?.toBranch?.guid;
      if (g && !seen.has(g)) seen.set(g, e?.toBranch?.name || "—");
    });
    return [
      {
        key: "toBranches",
        label: "To Branch",
        items: [...seen].map(([v, t]) => ({ v, t })),
        match: (r, v) => v.includes(r.toBranchGuid),
      },
    ];
  }, [stockList]);

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.toBranch) ||
      hit(r.remarks) ||
      hit(r.date) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code) || hit(l?.batch))
    );
  };

  const drawerContent = (r) => ({
    title: r.toBranch ? `To ${r.toBranch}` : "Stock transfer",
    subtitle: [r.billNo, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.toBranch || r.billNo,
    meta: [
      { k: "From branch", v: branch?.name },
      { k: "To branch", v: r.toBranch },
      { k: "Date", v: dmy(r.date) },
      { k: "Remarks", v: r.remarks },
      { k: "Items", v: intg(r.items) },
    ],
    lines: {
      title: "Transfer lines",
      columns: [{ t: "Item" }, { t: "Batch" }, { t: "Qty", align: "right" }],
      rows: r.lines.map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        l?.batch || "—",
        intg(l?.quantity),
      ]),
    },
    totals: [{ l: "Total quantity", v: intg(r.qty) }],
  });

  return (
    <TransactionScreen
      title="Stock Transfer"
      subtitle="Manage Stock Transfers here"
      unit="transfers"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, branch, remarks, item…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "billNo:-1", t: "Entry no (high–low)" },
        { v: "billNo:1", t: "Entry no (low–high)" },
        { v: "toBranch:1", t: "To branch A–Z" },
      ]}
      exportName="Stocktransfer"
      onRefresh={handlestockData}
      canAdd={permit?.add}
      newLabel="New transfer"
      onNew={() => {
        setModalMode("add");
        setModalOpen(true);
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit transfer"
      canDelete={permit?.deletion}
      // the old list passed a reshaped row without `id`; kept as it was
      onDelete={(r) => handleDeleteClick({ guid: r.guid })}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <StockTransferModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlestockData();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default StockTransfer;
